import { Reference } from '@ninetailed/experience.js';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';
import { useMemo } from 'react';
import { NAVIGATION_ELEMENT_CONFIGS, NavigationExperiments } from './data';
import { ElementsByConfig } from './use-navigation-data';
import { useNavigationExperiment } from './use-navigation-experiment';
import { EMPTY_REFERENCE } from './utils/empty-reference';
import { getExperimentData, getNavigationConfigKey } from './utils/experiment-helpers';

/**
 * Custom hook to manage navigation experiments
 */
export const useNavigationExperiments = (
  elementsByConfig: Map<string, ElementsByConfig>
): NavigationExperiments => {
  // Pre-calculate experiment data to avoid recalculating on each render
  const experimentDataMemo = useMemo(() => {
    const data: Record<
      string,
      {
        baseline: Reference;
        experiences: ExperienceConfiguration<Reference>[];
        experimentName?: string;
      }
    > = {};
    NAVIGATION_ELEMENT_CONFIGS.forEach((config) => {
      const key = getNavigationConfigKey(config);
      data[key] = getExperimentData({
        type: config.type,
        petType: config.petType,
        targetGroup: config.targetGroup,
        elementsByConfig,
      });
    });
    return data;
  }, [elementsByConfig]);

  // Call useNavigationExperiment for each navigation element at the hook level (React Hook rules require fixed calls)
  const desktopDogNewExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.DESKTOP_DOG_NEW_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.DESKTOP_DOG_NEW_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.DESKTOP_DOG_NEW_CUSTOMER?.experimentName,
  });
  const desktopDogExistingExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.DESKTOP_DOG_EXISTING_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.DESKTOP_DOG_EXISTING_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.DESKTOP_DOG_EXISTING_CUSTOMER?.experimentName,
  });
  const desktopCatNewExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.DESKTOP_CAT_NEW_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.DESKTOP_CAT_NEW_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.DESKTOP_CAT_NEW_CUSTOMER?.experimentName,
  });
  const desktopCatExistingExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.DESKTOP_CAT_EXISTING_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.DESKTOP_CAT_EXISTING_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.DESKTOP_CAT_EXISTING_CUSTOMER?.experimentName,
  });
  const mobileDogNewExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.MOBILE_DOG_NEW_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.MOBILE_DOG_NEW_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.MOBILE_DOG_NEW_CUSTOMER?.experimentName,
  });
  const mobileDogExistingExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.MOBILE_DOG_EXISTING_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.MOBILE_DOG_EXISTING_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.MOBILE_DOG_EXISTING_CUSTOMER?.experimentName,
  });
  const mobileCatNewExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.MOBILE_CAT_NEW_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.MOBILE_CAT_NEW_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.MOBILE_CAT_NEW_CUSTOMER?.experimentName,
  });
  const mobileCatExistingExperiment = useNavigationExperiment({
    baseline: experimentDataMemo.MOBILE_CAT_EXISTING_CUSTOMER?.baseline || EMPTY_REFERENCE,
    experiences: experimentDataMemo.MOBILE_CAT_EXISTING_CUSTOMER?.experiences || [],
    experimentName: experimentDataMemo.MOBILE_CAT_EXISTING_CUSTOMER?.experimentName,
  });

  // Create experiments map with optimized dependencies
  return useMemo(() => {
    const experimentMap: NavigationExperiments = {};

    const experimentMappings = [
      { key: 'DESKTOP_DOG_NEW_CUSTOMER', experiment: desktopDogNewExperiment },
      {
        key: 'DESKTOP_DOG_EXISTING_CUSTOMER',
        experiment: desktopDogExistingExperiment,
      },
      { key: 'DESKTOP_CAT_NEW_CUSTOMER', experiment: desktopCatNewExperiment },
      {
        key: 'DESKTOP_CAT_EXISTING_CUSTOMER',
        experiment: desktopCatExistingExperiment,
      },
      { key: 'MOBILE_DOG_NEW_CUSTOMER', experiment: mobileDogNewExperiment },
      {
        key: 'MOBILE_DOG_EXISTING_CUSTOMER',
        experiment: mobileDogExistingExperiment,
      },
      { key: 'MOBILE_CAT_NEW_CUSTOMER', experiment: mobileCatNewExperiment },
      {
        key: 'MOBILE_CAT_EXISTING_CUSTOMER',
        experiment: mobileCatExistingExperiment,
      },
    ];

    experimentMappings.forEach(({ key, experiment }) => {
      const elementData = elementsByConfig.get(key);
      if (elementData) {
        experimentMap[key] = {
          element: elementData.element,
          experiment,
        };
      }
    });

    return experimentMap;
  }, [
    elementsByConfig,
    desktopDogNewExperiment,
    desktopDogExistingExperiment,
    desktopCatNewExperiment,
    desktopCatExistingExperiment,
    mobileDogNewExperiment,
    mobileDogExistingExperiment,
    mobileCatNewExperiment,
    mobileCatExistingExperiment,
  ]);
};
